import React from 'react';
import { Box, Typography, TextField } from '@mui/material';

/**
 * Read-only, pretty-printed JSON viewer for a text/jsonb column that stores
 * a JSON string (or object). Used for fields that are derived/generated
 * (e.g. by a backfill) rather than hand-authored, so editing happens by
 * re-running the source process, not by typing into this form.
 *
 * Renders nothing if the value is null/blank -- callers can wrap this in
 * their own "no schema yet" messaging if desired.
 */
function ReadOnlyJsonField({ label, value, minRows = 3, maxRows = 12 }) {
  if (value === null || value === undefined || value === '') {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', mt: 0.5 }}>
          Not yet generated
        </Typography>
      </Box>
    );
  }

  let pretty;
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    pretty = JSON.stringify(parsed, null, 2);
  } catch {
    // Not valid JSON -- show the raw string rather than hiding it
    pretty = typeof value === 'string' ? value : String(value);
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <TextField
        fullWidth
        multiline
        minRows={minRows}
        maxRows={maxRows}
        value={pretty}
        InputProps={{
          readOnly: true,
          sx: { fontFamily: 'monospace', fontSize: 12.5, bgcolor: 'action.hover' },
        }}
        variant="outlined"
        size="small"
        sx={{ mt: 0.5 }}
        onFocus={(e) => e.target.select()}
      />
    </Box>
  );
}

export default ReadOnlyJsonField;
